import { ApiError } from '@api'
import { ApiRequestOptions } from '@root/api/core/ApiRequestOptions'
import { ApiResult } from '@root/api/core/ApiResult'
import { useEventBus } from '@root/context/EventProvider'
import { intro } from '@root/utils/routes'
import { useTabApiForm } from '@services/TabApiProvider/hooks/useTabApiForm'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { useToggle } from 'react-use'
import { useTabApiConfig } from '../../services/TabApiProvider/ProviderConfigs'
import { ErrorModal } from '../ErrorModal'
import { useHandleAppointmentError } from './useHandleAppointmentError'

export const GlobalErrorModal: FC = () => {
  const [isErrorModalVisible, toggleErrorModalVisibility] = useToggle(false)
  const history = useHistory()

  const { error: errorTabConfig } = useTabApiConfig()
  const { error: errorTabForm } = useTabApiForm()

  const [apiAppointmentError, setApiAppointmentError] = useState<ApiResult>()

  const { subscribe } = useEventBus()

  const handleSetApiAppointmentError = useCallback(
    ({
      result,
      options,
    }: {
      result: ApiResult
      options: ApiRequestOptions
    }) => {
      // endpoint should match the following pattern: appointment/:appointmentId/detail
      const matchesEndpointAppointmentDetails = options.path.match(
        /([^/]+)\/appointment\/([^/]+)\/detail/
      )
      if (result && matchesEndpointAppointmentDetails) {
        toggleErrorModalVisibility(true)
        setApiAppointmentError(result)
      }
    },
    [toggleErrorModalVisibility]
  )

  useEffect(
    () => subscribe('api-error', handleSetApiAppointmentError),
    [subscribe, handleSetApiAppointmentError]
  )

  const error = useMemo(
    () =>
      (apiAppointmentError
        ? ({
            ...apiAppointmentError,
            name: 'Appointment API Error',
          } as ApiError)
        : false) ||
      errorTabConfig ||
      errorTabForm,
    [apiAppointmentError, errorTabConfig, errorTabForm]
  )

  const { errorMessage } = useHandleAppointmentError(apiAppointmentError)

  useEffect(() => {
    if (error) {
      toggleErrorModalVisibility(true)
      console.error(error)
    } else {
      toggleErrorModalVisibility(false)
    }
  }, [error, toggleErrorModalVisibility])

  const handleClose = () => {
    toggleErrorModalVisibility(false)
    setApiAppointmentError(undefined)
    history.push(intro())
  }

  return (
    <ErrorModal
      isOpen={isErrorModalVisible}
      onRequestClose={handleClose}
      error={error}
      errorMessage={errorMessage}
    />
  )
}
